import { useState, useEffect, useRef } from "react";
import { lineGreenTick, triangleBlueDown } from "../../../assets/images";
import { dsButtonDisabledPrimary, dsButtonPrimary, dsButtonTextAction, joinButtonClasses } from "../../../utils/buttonStyles";

const RoundedMultiSelect = ({
  selectValues = [], // controlled selection
  selectOptions = [], // [{ label, value }]
  placeholder = "Select a company",
  selectHandler = () => {},
  classes = "",
  listClasses = "w-64",
}) => {
  const [open, setOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState(selectValues || []);
  const [searchTerm, setSearchTerm] = useState("");

  const wrapperRef = useRef(null);

  const toggleSelect = () => {
    setOpen((prev) => !prev);
    setTempSelected(selectValues); // sync selections when reopening
    setSearchTerm(""); // reset search
  };

  const handleSelectAll = (e) => {
    e.stopPropagation();
    const allValues = selectOptions.map((opt) => opt.value);
    setTempSelected(allValues);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    setTempSelected([]);
  };

  const handleItemClick = (value, e) => {
    e.stopPropagation();
    setTempSelected((prevSelected) => {
      const isAlreadySelected = prevSelected.includes(value);
      return isAlreadySelected
        ? prevSelected.filter((v) => v !== value)
        : [...prevSelected, value];
    });
  };

  const handleApply = (e) => {
    e.stopPropagation();
    selectHandler(tempSelected);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setTempSelected([]);
    selectHandler([]);
    setOpen(false);
  };

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Display label logic
  let displayLabel = placeholder;
  if (selectValues.length > 0) {
    if (selectValues.length === selectOptions.length) {
      displayLabel = "All Selected";
    } else if (selectValues.length === 1) {
      const selectedOption = selectOptions.find(
        (opt) => opt.value === selectValues[0]
      );
      displayLabel = selectedOption ? selectedOption.label : placeholder;
    } else {
      displayLabel = `${selectValues.length} Selected`;
    }
  }

  // Filtered options based on search term
  const filteredOptions = selectOptions.filter((item) =>
    (item.label || item.name || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <div
      ref={wrapperRef}
      className={`relative inline-flex py-2.5 px-4 items-center justify-between bg-white rounded-md cursor-pointer border border-gray-200 shadow-sm transition-all duration-200 hover:border-blue-200 ${classes}`}
      style={{ minWidth: "215px", paddingLeft: 14, paddingRight: 14 }}
      onClick={toggleSelect}
    >
      <h2 className="font-fig-semibold text-gray-800 truncate mb-0">
        {displayLabel}
      </h2>
      <img
        src={triangleBlueDown}
        alt="dropdown-icon"
        className={`ml-2 transition-transform duration-200 ${
          open ? "rotate-180 transform" : ""
        }`}
      />

      {open && (
        <div
          className={`absolute z-20 bg-white rounded-lg shadow-lg border border-gray-200 top-12 right-0 overflow-hidden flex flex-col ${listClasses}`}
          style={{ maxHeight: "320px" }}
        >
          {/* Header actions */}
          <div className="flex justify-between px-4 py-2 text-xs text-blue-600 font-fig-semibold border-b border-gray-200 sticky top-0 bg-white z-10">
            <button onClick={handleSelectAll} className={joinButtonClasses(dsButtonTextAction, 'text-xs')}>
              Select All
            </button>
            <button
              onClick={handleClearAll}
              className={joinButtonClasses(dsButtonTextAction, 'text-xs text-red-500 hover:text-red-600')}
            >
              Clear All
            </button>
          </div>

          {/* Search Input */}
          <div className="px-4 py-2 border-b border-gray-200 sticky top-8 bg-white z-10">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Search..."
              className="w-full text-xs rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:border-gray-900"
            />
          </div>

          {/* Options List */}
          <ul
            className="overflow-y-auto flex-1"
            style={{
              scrollbarWidth: "thin",
              maxHeight: "200px",
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((item, index) => {
                const label = item.label || item.name || item;
                const value = item.value || item.id || item;
                const isSelected = tempSelected.includes(value);
                return (
                  <li
                    key={value || index}
                    onClick={(e) => handleItemClick(value, e)}
                    className={`flex items-center justify-between px-5 py-2.5 text-xs font-fig-semibold cursor-pointer ${
                      isSelected ? "bg-blue-50 text-gray-900" : "text-gray-600"
                    } hover:bg-blue-100`}
                  >
                    <span className="truncate">{label}</span>
                    <span className="flex-shrink-0 w-6 h-6 ml-2 flex items-center justify-center">
                    {isSelected ? (
                      <img
                        src={lineGreenTick}
                        alt="selected"
                        className="h-4 w-4 ml-2"
                      />
                    )
                  : (<svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 ml-2 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <rect x="4" y="4" width="16" height="16" rx="3" ry="3" stroke="currentColor" />
                      </svg>
                    )}
                    </span>
                  </li>
                );
              })
            ) : (
              <li className="px-5 py-3 text-xs text-gray-400 text-center">
                No results found
              </li>
            )}
          </ul>

          {/* Footer with Apply */}
          <div className="flex justify-end gap-3 px-4 py-2 border-t border-gray-200 bg-gray-50 sticky bottom-0">
            <button
              onClick={handleClear}
              className={joinButtonClasses(dsButtonTextAction, 'text-xs text-red-500 hover:text-red-600', tempSelected.length === 0 && 'opacity-50 cursor-not-allowed')}
              disabled={tempSelected.length === 0}
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              disabled={tempSelected.length === 0}
              className={joinButtonClasses(dsButtonPrimary, 'h-8 px-3 text-xs', tempSelected.length === 0 && dsButtonDisabledPrimary)}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoundedMultiSelect;
